import React, { useEffect, useMemo, useState } from 'react';
import { ReptoolModal } from '../utilities/ReptoolModal.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { Gene, isChanceGene, geneExpressions } from '../../types/Gene.ts';
import { GeneCalculator } from '../../utils/GeneCalculator.ts';
import { LoadingSpinner } from '../Loading.tsx';
import { driver } from 'driver.js';
import { Morph } from '../../types/Morph.ts';
import { Tab, Tabs } from 'react-bootstrap';
import { useRepToolContext } from '../../RepToolContext.tsx';
import { PhenotypeCalculator } from '../../utils/PhenotypeCalculator.ts';
import { ResultTile } from '../clutches/modals/HatchedClutchModal.tsx';
import { makeMorphNameStringPotential } from "../../utils/GeneCalculator.ts";
import { FindMorphByGenes } from "../utilities/FindMorph.ts";
import Lightbox from "yet-another-react-lightbox";

export interface Parent {
  morph_name: string;
  genes: Gene[];
}

export const ResultItem = ({ result, chance, morphs }) => {
  const [openLightbox, setOpenLightbox] = useState(false);
  const morph = morphs.find(x => x.name === result.finalResults);
  if (morph) {
    result.finalResults = morph.name;
  } else if (result.finalResults === "" && result.homs.length !== 0 && !morph) {
    result.finalResults = result.homs.join(", ");

  } else if (result.finalResults === "") {
    // hopefully this only happens on both wild type parents
    result.finalResults = "Wild Type";
  }
  const picture = morph && morph.pictures.length > 0 ? morph.pictures[0] : { url: "images/notavailable.jpg", credit: "" };
  const pictureSlides = morph?.pictures.map((picture) => {
    return {
      src: picture.url,
      alt: picture.credit,
    }
  }) || [];

  return (
    <div className="mb-2 col-6 col-md-4 col-lg-3 px-1 rep-result result-item text-center border result-border">
      <Lightbox
        open={openLightbox}
        close={() => setOpenLightbox(false)}
        slides={pictureSlides}
      />
      <div className="w-100">
        <div
          onClick={() => pictureSlides.length > 0 ? setOpenLightbox(true) : null}
          style={{
            height: '200px',
            overflow: 'hidden',
            backgroundImage: `url(${morph?.pictures?.[0]?.url || "images/notavailable.jpg"})`,
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          }}>

        </div>
        <p style={{ lineHeight: 1 }}><small className="calculator-credit">{picture.credit}</small></p>
        <div className="fraction d-flex flex-row text-center">
          <div>{(chance).toFixed(2)}%</div>
        </div>
        <div className="">
          <h5>{result.finalResults}{result.hets.length > 0 ? ` het ${result.hets.join(", ")}` : ''}</h5>
        </div>
      </div>
    </div >
  )
}

const ParentComponent = ({ parent, morphs }) => {
  const [pictureLoaded, setPictureLoaded] = useState(false);
  const name = parent.morph_name;
  let splitName = name.split(" het ");
  splitName[0] = splitName[0] ? splitName[0] : "Wild Type";

  const foundMorph = morphs.find(morph => morph.name === splitName[0]);

  const picture = foundMorph && foundMorph.pictures.length > 0 ? foundMorph.pictures[0] : { url: "images/notavailable.jpg", credit: "" };
  return (
    <div className="male p-0 mx-auto flex-grow parent">
      <img id="male-picture" className={`default-image w-100 ${!pictureLoaded ? 'd-none' : ''}`} src={picture.url} onLoad={() => setPictureLoaded(true)} alt={splitName[0]} />
      <div className={`default-image ${pictureLoaded ? 'd-none' : ''}`}>
        <LoadingSpinner />
      </div>
      {pictureLoaded && <p className='text-center' style={{ lineHeight: 1 }}><small id="male-credit" className="text-center calculator-credit">{picture.credit}</small></p>}
    </div>
  )
}

const GenesToggle = ({ genes, toggleGene, selectedGenes }: {
  genes: Gene[],
  toggleGene: (gene: Gene) => void,
  selectedGenes: Gene[],
}) => {
  const hetGenes = genes.filter(gene => gene.gene_type === "Recessive" && (isChanceGene(gene) || gene.expression === "HET"));
  const fullExpressionGenes = genes.filter(gene => !hetGenes.includes(gene));
  return (
    <div className="genes-dialog-container mt-3 mx-auto">
      <div>Visual Genes</div>
      {
        fullExpressionGenes.map((gene, x) => {
          const enabled = selectedGenes.some(sGene => sGene.gene_name === gene.gene_name);
          const expression = geneExpressions.find(x => x.value === gene.expression);
          return (
            <div key={x} className="gene-container">
              <div className="gene-toggle d-flex flex-row">
                <input type="checkbox" checked={enabled} onChange={() => toggleGene(gene)} />
                <div className={`gene-name ml-2 ${enabled ? '' : 'text-muted'}`}>
                  {gene.gene_name}
                  {gene.expression && gene.expression !== "NORMAL" && <small> ({expression?.label})</small>}
                </div>
              </div>
            </div>
          )
        })
      }
      {fullExpressionGenes.length === 0 && <div className="text-muted">None</div>}
      <div className='mt-3'>Potential Hets</div>
      {hetGenes.length === 0 && <div className="text-muted">None</div>}
      {hetGenes.map((gene, x) => {
        const enabled = selectedGenes.some(sGene => sGene.gene_name === gene.gene_name);
        const expression = geneExpressions.find(x => x.value === gene.expression);
        return (
          <div key={x} className="gene-container">
            <div className="gene-toggle d-flex flex-row">
              <input type="checkbox" checked={enabled} onChange={() => toggleGene(gene)} />
              <div className={`gene-name ml-2 ${enabled ? '' : 'text-muted'}`}>
                {gene.gene_name}
                {gene.expression !== "NORMAL" && <small> ({expression?.label})</small>}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}



export const ResultsModal = ({ closeModal, parents = [], morphs, genes }: { closeModal: () => void, parents: Parent[], morphs: Morph[], genes: Gene[] }) => {
  const [fatherGenes, setFatherGenes] = useState<Gene[]>([]);
  const [motherGenes, setMotherGenes] = useState<Gene[]>([]);
  const { appendControls } = useRepToolContext();
  const father = { ...parents[0] };
  const mother = parents.length > 1 ? { ...parents[1] } : { ...parents[0] };

  const updateDriver = () => {
    return driver({
      steps: [
        { element: '#parents', popover: { title: 'Parents', description: 'This is the pairing that is being used to calculate the results' } },
        { element: '.genes-dialog-container', popover: { title: 'Genetics', description: 'These are the genes each parent has. Possible, 66% and 50% het genes are disabled on default, but you can toggle them to experiment with results.' } },
        { popover: { title: 'Genotype Results', description: 'These are the possible results of the pairing. The percentage is the chance of that result being produced. These results are odds of all combinations using punnett squares.' } },
        { popover: { title: 'Phenotype Results', description: 'These are the possible visual results of the pairing. The percentage is the chance of that result being produced. These results are simplified to be only what can be determined by looking at the animal. For example, a (het x het) pairing will show up as 2 results (visually expressed & 66% het) the genotype result would be all possible combos (expressed, het, and none).' } },
      ]
    });
  }

  const [driverObj] = useState(updateDriver());



  useEffect(() => {
    // filter out chance genes initially
    const fatherGenes = father?.genes?.filter(gene => !isChanceGene(gene) && gene.gene_name !== '') || [];
    const motherGenes = mother?.genes?.filter(gene => !isChanceGene(gene) && gene.gene_name !== '') || [];
    setFatherGenes(fatherGenes);
    setMotherGenes(motherGenes);
  }, [parents]);

  useEffect(() => {
    appendControls(null);
  }, []);


  const { geneCalcResults, phenotypeCalcResults } = useMemo(() => {
    const fGenes = fatherGenes.map(gene => {
      return {
        ...gene,
        isHet: gene.expression === "HET" || gene.expression === "SUPER" || isChanceGene(gene),
      }
    })
    const mGenes = motherGenes.map(gene => {
      return {
        ...gene,
        isHet: gene.expression === "HET" || gene.expression === "SUPER" || isChanceGene(gene),
      }
    })
    const geneCalcResults = GeneCalculator({ maleInput: fGenes, femaleInput: mGenes, morphs: morphs });
    const phenotypeCalcResults = PhenotypeCalculator({ genes: fGenes }, { genes: mGenes }, morphs).map((result) => {
      return {
        genes: result,
        displayName: makeMorphNameStringPotential(result, morphs),
        picture: FindMorphByGenes(result, morphs)?.pictures?.[0]?.url || null,
        quantity: 0
      };
    }
    ) || []
    return { geneCalcResults, phenotypeCalcResults };
  }, [fatherGenes, motherGenes])

  const allCount = geneCalcResults?.results.reduce((accumulator, result) => {
    return accumulator + result.multiplier;
  }, 0);

  const allGenes = [...fatherGenes, ...motherGenes];


  const hasLinebred = allGenes.some(gene => gene.gene_type === "(Line Bred)");
  const hasUnproven = allGenes.some(gene => gene.gene_type === "(Unproven)");
  return (
    <ReptoolModal title={"Results"} closeModal={closeModal} showTutorial={driverObj.drive} body={(
      <div id="results-container" className="flex-fill">
        <div id="parents" className="parents p-0 d-flex flex-row justify-content-around">
          <div className='d-flex flex-column flex-grow col-6 col-md-5'>
            <ParentComponent parent={father} morphs={morphs} />
            <GenesToggle genes={father.genes.filter(gene => gene.gene_name)} selectedGenes={fatherGenes} toggleGene={(gene: Gene) => {
              const foundGene = fatherGenes.find(x => x.gene_name === gene.gene_name);
              if (foundGene) {
                const index = fatherGenes.indexOf(foundGene);
                fatherGenes.splice(index, 1);
                setFatherGenes([...fatherGenes]);
              } else {
                setFatherGenes([...fatherGenes, gene]);
              }
            }} />
          </div>
          <div className="my-auto d-none d-md-block text-center">
            <FontAwesomeIcon icon={faTimes} size="2x" />
          </div>
          <div className='d-flex flex-column flex-grow col-6 col-md-5'>
            <ParentComponent parent={mother} morphs={morphs} />
            <GenesToggle genes={mother.genes.filter(gene => gene.gene_name)} selectedGenes={motherGenes} toggleGene={(gene: Gene) => {
              const foundGene = motherGenes.find(x => x.gene_name === gene.gene_name);
              if (foundGene) {
                const index = motherGenes.indexOf(foundGene);
                motherGenes.splice(index, 1);
                setMotherGenes([...motherGenes]);
              } else {
                setMotherGenes([...motherGenes, gene]);
              }
            }} />
          </div>
        </div>
        <hr />
        <Tabs defaultActiveKey="genotype" >
          <Tab id='genotype-tab' eventKey="genotype" title="Genotype Results" >
            <div id='genotype-results' className="d-flex flex-wrap">
              {geneCalcResults?.results.map((result, x) => (
                <ResultItem
                  key={x}
                  result={result}
                  chance={(result.multiplier / allCount) * 100}
                  morphs={morphs}
                />
              ))}
            </div>
          </Tab>
          <Tab id='phenotype-tab' eventKey="phenotype" title="Phenotype Results">
            <div id='phenotype-results' className='d-flex flex-wrap pt-3'>
              {phenotypeCalcResults.map((result, x) => {
                return (
                  <ResultTile key={x} result={result} morphs={morphs} genes={genes} />
                )
              })}
            </div>
          </Tab>
        </Tabs>

        <div className="text-center d-flex flex-column align-items-center mt-3 mb-5 pb-2">
          <div id="line-bred-disclaimer">
            {hasUnproven && <h6>* Please Note: In this pairing there was one or more Unproven or Progressive traits. This means that the results may or not be correct due to lack of information at this time.</h6>}
            {hasLinebred && <h6>* Please Note: When breeding line bred traits, the offspring may not express enough of the gene to be considered that line bred trait.</h6>}
          </div>
        </div>
      </div>
    )} />
  )
}